/**
 * Function makes an asynchronious rest.get call, and try to load template list from the server.
 */
import { PROPERTY_NAME, PROPERTY_VALUE, TYPE_KEY } from "../common/constant";
import { beginLoading, endLoading } from "./loadingSpinner";
import { createBackendErrorMessages, createErrorMessages, messagesReceived } from "./message";
import { addLockInfos, propertyChange as runnerPropertyChanged, SHOW_READ_ONLY_DIALOG } from "./surveyRunner";

import { RespondentsOverviewModel as roModel } from "../common/entities";
import navigate from "../common/navigate";
import rest from "../common/rest";
import ObjectUtils, { getEndTimeFromYear, getStartTimeFromYear } from "./../utils/ObjectUtils";
import SurveyUtils from "./../utils/SurveyUtils";
import { resetSurvey as resetSurveyEditor } from "./surveyEditor";
import { resetSurveyView } from "./surveyView";

const { getDeepProperty } = ObjectUtils;
const LOAD_ACTION = "RESPONDENT.LOAD_ACTION";
const SELECT_OVERVIEW = "RESPONDENT.SELECT_OVERVIEW";
const LOAD_DETAILS_ACTION = "RESPONDENT.LOAD_DETAILS";
const SORT_COLUMN_ACTION = "RESPONDENT.SORT_COLUMN_ACTION";
const PROPERTY_CHANGED_ACTION = "RESPONDENT.PROPERTY_CHANGED_ACTION";
const LOAD_RESPONDENTS_LIST_ACTION = "RESPONDENT.LOAD_RESPONDENTS_LIST_ACTION";
const VALUE = "value";
const TABLE_TYPE = "tableType";

export const LIST = "list";
export const LOADED = "loaded";
export const CURRENT_OVERVIEW = "currentOverview";
export const DETAILS = "details";
export const RESPONDENT = "respondent";
export const RESPONDENT_LIST = "respondentList";

export const SORT_TABLE_TEMPLATE = "sortTableTemplate";
export const SORT_TABLE_TEMPLATE_BY = "sortTableTemplateBy";
export const SORT_TABLE_TEMPLATE_ASCENDING = "sortTableTemplateAscending";
export const REFRESH_DETAILS_PAGE = "refreshDetailsPage";
export const PROJECT_FOREGIN_ID = "projectForeignId";
export const FILTERS = "filters";

const initialState = {
  [LIST]: [],
  [DETAILS]: [],
  [CURRENT_OVERVIEW]: {},
  [SORT_TABLE_TEMPLATE]: false,
  [SORT_TABLE_TEMPLATE_ASCENDING]: true,
  [SORT_TABLE_TEMPLATE_BY]: "",
  [REFRESH_DETAILS_PAGE]: false,
  [FILTERS]: {
    periodStart: "",
    periodEnd: "",
    status: "",
    searchQuery: ""
  }
};

export default function reducer(state = initialState, action) {
  let newState;
  switch (action[TYPE_KEY]) {
    case LOAD_ACTION: {
      let sortedTemplateList = SurveyUtils.handleSortTemplateList(
        action[VALUE],
        state[SORT_TABLE_TEMPLATE_BY] || "lastEdit",
        state[SORT_TABLE_TEMPLATE_BY] ? !!state[SORT_TABLE_TEMPLATE_ASCENDING] : false
      );
      newState = Object.assign({}, state, {
        [LIST]: sortedTemplateList
      });
      break;
    }
    case LOAD_DETAILS_ACTION:
      newState = Object.assign({}, state, {
        [DETAILS]: action[VALUE]
      });
      break;
    case LOAD_RESPONDENTS_LIST_ACTION:
      newState = Object.assign({}, state, {
        [RESPONDENT_LIST]: action[VALUE]
      });
      break;
    case SELECT_OVERVIEW:
      newState = Object.assign({}, state, { [CURRENT_OVERVIEW]: action[VALUE] });
      break;
    case SORT_COLUMN_ACTION: {
      let listType = action[TABLE_TYPE];
      let sortedTemplateList = SurveyUtils.handleSortTemplateList(
        state[listType],
        action[SORT_TABLE_TEMPLATE_BY],
        action[SORT_TABLE_TEMPLATE_ASCENDING]
      );
      newState = Object.assign({}, state, {
        [listType]: sortedTemplateList,
        [SORT_TABLE_TEMPLATE]: true,
        [SORT_TABLE_TEMPLATE_BY]: action[SORT_TABLE_TEMPLATE_BY],
        [SORT_TABLE_TEMPLATE_ASCENDING]: action[SORT_TABLE_TEMPLATE_ASCENDING]
      });
      break;
    }
    case PROPERTY_CHANGED_ACTION:
      newState = Object.assign({}, state, {
        [action[PROPERTY_NAME]]: action[PROPERTY_VALUE]
      });
      break;
    default:
      newState = state;
      break;
  }
  return newState;
}

export function loadOverview() {
  return async (dispatch, getState) => {
    try {
      // 🚀 Show spinner immediately
      dispatch(beginLoading());

      // Reset local state
      dispatch(resetSurveyView());
      dispatch(resetSurveyEditor());

      // Prepare query filter
      const filter = generateFilter(getState().fundingArea);

      // 🔄 Fetch overview data directly
      const json = await rest.doGet(`/survey/surveys/overviews${filter}`);

      if (json?.overviews) {
        dispatch(loadSuccessful(json.overviews, LOAD_ACTION));
      } else if (json) {
        dispatch(messagesReceived(createErrorMessages(json)));
      }
    } catch (error) {
      // ❌ Handle unexpected errors (network, etc.)
      dispatch(messagesReceived(createBackendErrorMessages(error)));
    } finally {
      // 🧹 Ensure loading spinner stops in all cases
      dispatch(endLoading());
    }
  };
}

function generateFilter(state) {
  let filter = "";
  if (getDeepProperty(state, "selectedFundingArea", "overview", "id")) {
    filter = `?fundingAreaRefId=${state.selectedFundingArea.overview.id}`;
    if (getDeepProperty(state, "selectedProgram", "overview", "id")) {
      filter = `${filter}&programRefId=${state.selectedProgram.overview.id}`;
      if (getDeepProperty(state, "selectedProgramInstance", "overview", "id")) {
        filter = `${filter}&instanceRefId=${state.selectedProgramInstance.overview.id}`;
        if (getDeepProperty(state, "selectedProject", "overview", "id")) {
          filter = `${filter}&projectRefId=${state.selectedProject.overview.id}`;
        }
      }
    }
  }
  return filter;
}

/**
 * Function makes an asynchronous rest.get call, and try to load template list from the server.
 */
export function loadDetails(respondentOverview, refreshCurrentPage) {
  return async (dispatch) => {
    const reportPeriodGid = respondentOverview[roModel.reportPeriodGid];

    try {
      // 🚀 Show spinner immediately
      dispatch(beginLoading());

      // Update overview selection
      if (refreshCurrentPage) {
        dispatch(loadCurrentOverview(reportPeriodGid));
      } else {
        dispatch(selectOverview(respondentOverview));
      }

      // Preload respondent list
      dispatch(loadRespondentList(respondentOverview.surveyId));


      // 🔄 Fetch details directly
      const json = await rest.doGet(`/survey/surveys/overviews/${reportPeriodGid}/details`);

      if (json?.details) {
        dispatch(loadSuccessful(json.details, LOAD_DETAILS_ACTION));
        dispatch(getIsItLocked(reportPeriodGid));

        // Navigate early to details page
        navigate.to("/userArea/respondentDetails", { replace: true, state: true });
      } else if (json) {
        dispatch(messagesReceived(createErrorMessages(json)));
      }
    } catch (error) {
      dispatch(messagesReceived(createBackendErrorMessages(error)));
    } finally {
      // ✅ Ensure spinner stops regardless of success or failure
      dispatch(endLoading());
    }
  };
}



/**
 * Function makes an asynchronious rest.get call, and try to current overview.
 */
export function loadCurrentOverview(reportPeriodGid) {
  return (dispatch) => {
    dispatch(beginLoading());
    rest.doGet(`/survey/surveys/overviews/${reportPeriodGid}`).then(
      (json) => {
        if (json) {
          dispatch(selectOverview(json));
          dispatch(endLoading());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

/**
 * Function makes an asynchronious rest.get call, and gets info is current overviewLocked.
 */
export function getIsItLocked(reportPeriodGid) {
  return (dispatch) => {
    dispatch(beginLoading());
    rest.doGet(`/survey/surveys/overviews/${reportPeriodGid}/lock`).then(
      (json) => {
        if (json) {
          dispatch(addLockInfos(json));
          dispatch(runnerPropertyChanged(SHOW_READ_ONLY_DIALOG, false));
          dispatch(endLoading());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
          dispatch(endLoading());
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
        dispatch(endLoading());
      }
    );
  };
}

export const sortColumn = (columnType, acs, tableType) => {
  return {
    [TYPE_KEY]: SORT_COLUMN_ACTION,
    [SORT_TABLE_TEMPLATE_BY]: columnType,
    [SORT_TABLE_TEMPLATE]: true,
    [SORT_TABLE_TEMPLATE_ASCENDING]: acs,
    [TABLE_TYPE]: tableType
  };
};

export function selectOverview(respondentOverview) {
  return {
    [TYPE_KEY]: SELECT_OVERVIEW,
    [VALUE]: respondentOverview
  };
}

function loadSuccessful(data, actionType) {
  return {
    [TYPE_KEY]: actionType,
    [VALUE]: data
  };
}

export function propertyChange(propertyName, propertyValue) {
  return {
    [TYPE_KEY]: PROPERTY_CHANGED_ACTION,
    [PROPERTY_NAME]: propertyName,
    [PROPERTY_VALUE]: propertyValue
  };
}

export function respondentForPagePropertyChange(propertyName) {
  return (dispatch, getState) => {
    let currentOverview = getState().respondentsOverview;
    let currentOverviewId = getState().respondentsOverview.currentOverview.reportPeriodGid;
    let respondent = currentOverview.respondent;
    const pageRespondent = { userId: respondent };
    rest
      .doPut(
        `/survey/surveys/overviews/${currentOverviewId}/details/${propertyName.pageGid}/respondent/`,
        JSON.stringify(pageRespondent)
      )
      .then(
        (json) => {
          if (json.status === 200) {
            dispatch(loadDetails(currentOverview.currentOverview));
          } else {
            dispatch(messagesReceived(createErrorMessages(json)));
          }
        },
        (reason) => {
          dispatch(messagesReceived(createBackendErrorMessages(reason)));
        }
      );
  };
}

/**
 * Function gets a list of respondents
 */
export function loadRespondentList() {
  return (dispatch, getState) => {
    let currentOverview = getState().respondentsOverview.currentOverview;
    rest.doGet(`/survey/surveys/overviews/${currentOverview[roModel.projectForeignId]}/respondents`).then(
      (json) => {
        if (json.respondentUsers) {
          dispatch(loadSuccessful(json, LOAD_RESPONDENTS_LIST_ACTION));
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
      }
    );
  };
}

/**
 * Function makes an asynchronious rest.delete call
 */
export function deleteReport(overviewId) {
  return (dispatch) => {
    rest.doPut(`/survey/surveys/overviews/${overviewId}/status/OBSOLETE`).then(
      (json) => {
        if (json) {
          dispatch(loadOverview());
        } else {
          dispatch(messagesReceived(createErrorMessages(json)));
        }
      },
      (reason) => {
        dispatch(messagesReceived(createBackendErrorMessages(reason)));
      }
    );
  };
}

export function downloadReportingStatus(filter) {
  return (dispatch, getState) => {
    dispatch(beginLoading());
    const fundingArea = getState().fundingArea;
    const selectedFundingArea = fundingArea.selectedFundingArea;
    let selectedFundingAreaRefId = selectedFundingArea?.overview?.id ?? null
    const selectedProgram = fundingArea.selectedProgram;
    let selectedProgramRefId = selectedProgram?.overview?.id ?? null
    let programInstanceRefId = fundingArea.selectedProgramInstance?.overview?.id ?? null;

    if (true) {
      let url = `/survey/evaluations/reportingStatus?`;

      if (selectedFundingAreaRefId){
        url += `fundingAreaRefId=${selectedFundingAreaRefId}`;
      }

      if (selectedProgramRefId) {
        url += `&programRefId=${selectedProgramRefId}`;
      }
      if (programInstanceRefId) {
        url += `&instanceRefId=${programInstanceRefId}`;
      }

      if (filter.status) {
        url += `&status=${filter.status}`;
      }
      if (filter.periodStart) {
        url += `&timeFacets[0].from=${getStartTimeFromYear(filter.periodStart)}`;
      }

      if (filter.periodEnd) {
        url += `&timeFacets[0].to=${getEndTimeFromYear(filter.periodEnd)}`;
      }

      rest.doGet(url, "text/csv").then(
        (response) => {
          try {
            let FileSaver = require("file-saver");
            let blob = new Blob(["\ufeff", response.response], { type: "text/csv;charset=utf-8" });
            let programName = "Statusbericht" + (selectedProgramRefId ? selectedProgram.overview.name : selectedFundingArea ? selectedFundingArea.overview.name : "");
            let fileName = programName.replace(/\s/g, "_").substring(0, 50) + new Date().getTime();
            FileSaver.saveAs(blob, fileName + ".csv");
            dispatch(endLoading());
          } catch (err) {
            dispatch(messagesReceived(createErrorMessages(err)));
            dispatch(endLoading());
          }
        },
        (reason) => {
          dispatch(messagesReceived(createBackendErrorMessages(reason)));
          dispatch(endLoading());
        }
      );
    };
  }
}
